import { useState, useEffect } from "react";
import { FileText, FileSpreadsheet, FileType, Loader2, Download, Save, Trash2, GripVertical, Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const LABELS = {
  riclass: "Riclassificazioni (SP e CE)",
  indici: "Indici di bilancio + giudizio",
  confronto: "Confronto pluriennale (grafici)",
  rendiconto: "Rendiconto finanziario",
  dscr: "DSCR prospettico",
  note: "Note e commenti",
};
const DEFAULT_ORDER = ["confronto", "riclass", "indici", "rendiconto", "dscr", "note"];
const ALL_ON = { riclass: true, indici: true, confronto: true, rendiconto: true, dscr: true, note: true, cover: true, panoramica: true };

export function ExportDialog({ trigger, onExport, exporting, years = [], defaultYear }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(ALL_ON);
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [year, setYear] = useState(defaultYear);
  const [presets, setPresets] = useState({});
  const [defaultPreset, setDefaultPreset] = useState("");
  const [presetName, setPresetName] = useState("");
  const [dragKey, setDragKey] = useState(null);
  const [lang, setLang] = useState("it");
  const [office, setOffice] = useState("it");

  useEffect(() => { if (defaultYear) setYear(defaultYear); }, [defaultYear]);
  useEffect(() => {
    if (!open) return;
    api.get("/export-presets").then((r) => {
      const ps = r.data?.presets || {};
      const def = r.data?.default || "";
      setPresets(ps);
      setDefaultPreset(def);
      if (def && ps[def]) applyPresetObj(ps[def]);
    }).catch(() => {});
  }, [open]);

  const toggle = (k) => setSel((s) => ({ ...s, [k]: !s[k] }));
  const anySelected = DEFAULT_ORDER.some((k) => sel[k]);
  const run = (fmt) => onExport(fmt, sel, year, order, { lang, office });

  const onDrop = (target) => {
    if (!dragKey || dragKey === target) return;
    setOrder((prev) => {
      const arr = prev.filter((k) => k !== dragKey);
      const idx = arr.indexOf(target);
      arr.splice(idx, 0, dragKey);
      return arr;
    });
    setDragKey(null);
  };

  const applyPresetObj = (p) => {
    if (!p) return;
    setSel({ ...ALL_ON, ...(p.sections || {}) });
    setLang(p.lang === "en" ? "en" : "it");
    setOffice(p.office === "uk" ? "uk" : "it");
    if (Array.isArray(p.order) && p.order.length) {
      const ord = p.order.filter((k) => DEFAULT_ORDER.includes(k));
      DEFAULT_ORDER.forEach((k) => { if (!ord.includes(k)) ord.push(k); });
      setOrder(ord);
    }
  };
  const applyPreset = (name) => applyPresetObj(presets[name]);

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) return;
    try {
      const { data } = await api.put("/export-presets", { name, sections: sel, order, lang, office });
      setPresets(data.presets || {});
      setPresetName("");
      toast.success("Preset salvato sul profilo studio");
    } catch { toast.error("Errore nel salvataggio del preset"); }
  };

  const setAsDefault = async (name) => {
    try {
      await api.put("/export-presets-default", { name: name === defaultPreset ? "" : name });
      setDefaultPreset((d) => (d === name ? "" : name));
      toast.success(name === defaultPreset ? "Preset predefinito rimosso" : "Preset impostato come predefinito");
    } catch { toast.error("Errore"); }
  };

  const deletePreset = async (name) => {
    try {
      const { data } = await api.delete(`/export-presets/${encodeURIComponent(name)}`);
      setPresets(data.presets || {});
      if (defaultPreset === name) setDefaultPreset("");
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Download className="h-5 w-5 text-primary" /> Esporta Report</DialogTitle>
          <DialogDescription>Scegli esercizio, sezioni (trascina per ordinarle) e preset.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          {years.length > 0 && (
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-24 font-mono" data-testid="export-year-select"><SelectValue placeholder="Anno" /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)} data-testid={`export-year-${y}`}>{y}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Select onValueChange={applyPreset}>
            <SelectTrigger className="flex-1" data-testid="export-preset-select"><SelectValue placeholder="Applica preset studio…" /></SelectTrigger>
            <SelectContent>
              {Object.keys(presets).length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">Nessun preset salvato</div>}
              {Object.keys(presets).map((n) => (
                <SelectItem key={n} value={n} data-testid={`export-preset-${n}`}>
                  {n} <span className="text-xs text-muted-foreground font-mono">· {presets[n]?.lang === "en" ? "EN" : "IT"} · {presets[n]?.office === "uk" ? "UK Office" : "Sede Italia"}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Lingua report</span>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger data-testid="export-lang-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="it" data-testid="export-lang-it">Italiano</SelectItem>
                <SelectItem value="en" data-testid="export-lang-en">English</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Sede firmataria</span>
            <Select value={office} onValueChange={setOffice}>
              <SelectTrigger data-testid="export-office-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="it" data-testid="export-office-it">Sede Italia</SelectItem>
                <SelectItem value="uk" data-testid="export-office-uk">UK Office</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <label data-testid="export-opt-cover" className="flex items-center gap-3 rounded-md border border-border/60 px-3 py-2 cursor-pointer hover:border-primary/50">
          <input type="checkbox" checked={!!sel.cover} onChange={() => toggle("cover")} className="h-4 w-4 accent-primary" data-testid="export-check-cover" />
          <span className="text-sm">Copertina (logo, titolo, indice sezioni)</span>
        </label>

        <label data-testid="export-opt-panoramica" className="flex items-center gap-3 rounded-md border border-border/60 px-3 py-2 cursor-pointer hover:border-primary/50">
          <input type="checkbox" checked={!!sel.panoramica} onChange={() => toggle("panoramica")} className="h-4 w-4 shrink-0 accent-primary" data-testid="export-check-panoramica" />
          <span className="text-sm">Panoramica pluriennale: rating per esercizio e 4 grafici (pagina dopo la copertina)</span>
        </label>

        <div className="space-y-2">
          {order.map((k) => (
            <div
              key={k}
              draggable
              onDragStart={() => setDragKey(k)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(k)}
              data-testid={`export-opt-${k}`}
              className={`flex items-center gap-2 rounded-md border px-2.5 py-2.5 transition-colors ${dragKey === k ? "border-primary bg-primary/5" : "border-border/60"}`}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground/60 cursor-grab shrink-0" />
              <input type="checkbox" checked={!!sel[k]} onChange={() => toggle(k)} className="h-4 w-4 accent-primary shrink-0" data-testid={`export-check-${k}`} />
              <span className="text-sm leading-snug">{LABELS[k]}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 border-t border-border/50 pt-3">
          <Input value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder="Salva come preset studio…" className="h-8 text-sm" data-testid="export-preset-name" />
          <Button variant="outline" size="sm" onClick={savePreset} disabled={!presetName.trim()} data-testid="export-preset-save"><Save className="h-4 w-4" /></Button>
        </div>
        {Object.keys(presets).length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {Object.keys(presets).map((n) => (
              <span key={n} className="inline-flex items-center gap-1 text-xs rounded-full border border-border/60 px-2 py-0.5">
                <button onClick={() => setAsDefault(n)} data-testid={`export-preset-default-${n}`} title="Imposta come predefinito"
                  className={defaultPreset === n ? "text-amber-500" : "text-muted-foreground hover:text-amber-500"}>
                  <Star className="h-3 w-3" fill={defaultPreset === n ? "currentColor" : "none"} />
                </button>
                {n}
                <button onClick={() => deletePreset(n)} data-testid={`export-preset-del-${n}`} className="text-muted-foreground hover:text-red-500"><Trash2 className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
          <Button variant="outline" onClick={() => run("pdf")} disabled={!anySelected || !!exporting} data-testid="export-pdf-confirm">
            {exporting === "pdf" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />} Scarica PDF
          </Button>
          <Button variant="outline" onClick={() => run("docx")} disabled={!anySelected || !!exporting} data-testid="export-docx-confirm">
            {exporting === "docx" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileType className="h-4 w-4 mr-2" />} Scarica Word
          </Button>
          <Button variant="outline" onClick={() => run("odt")} disabled={!anySelected || !!exporting} data-testid="export-odt-confirm" title="File .odt: si apre direttamente con Google Documenti, LibreOffice e Word">
            {exporting === "odt" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileType className="h-4 w-4 mr-2" />} Google Documenti (.odt)
          </Button>
          <Button onClick={() => run("excel")} disabled={!anySelected || !!exporting} data-testid="export-excel-confirm">
            {exporting === "excel" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />} Scarica Excel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
